import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { ProductDiscoverySource, ProductWithImage } from 'shared/src/api/types'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'
import { formattedUsd } from 'shared/src/utils/formatting'

import { addTranslationHelpers } from '../../../../utils/addTranslationHelpers'
import { sansSerifFontFamily } from '../../../../utils/styles'
import { BookmarkButton } from '../../../BookmarkButton'
import {
  FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX,
  FEED_RODEO_PRODUCT_IMAGE_Z_INDEX,
  FEED_RODEO_PRODUCT_NON_MATCH_OVERLAY_Z_INDEX,
} from '../../../../utils/zIndex'
import { Views } from 'components/Views'
import { LinkOrButton } from 'components/LinkOrButton'

export const WIDTH = 120
export const HEIGHT = 166

const useStyles = (useGradient: boolean) =>
  createUseStyles({
    container: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#2a2e31',
      width: 120,
      minWidth: 120,
      height: 166,
    },
    matchesSearch: {
      filter: 'drop-shadow(0px 0px 25px #CDAA7E)',
    },
    nonMatchOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: FEED_RODEO_PRODUCT_NON_MATCH_OVERLAY_Z_INDEX,
      backgroundColor: 'black',
      opacity: 0.3,
      pointerEvents: 'none',
    },
    imageContainer: {
      position: 'relative',
      height: useGradient ? 166 : 109,
      width: '100%',
      overflow: 'hidden',
      flexShrink: 0,
      backgroundColor: 'white',
    },
    image: {
      position: 'relative',
      height: useGradient ? 166 : 109,
      width: '100%',
      objectFit: 'contain',
      zIndex: FEED_RODEO_PRODUCT_IMAGE_Z_INDEX,
    },
    imageGradient: {
      position: 'absolute',
      top: 0,
      height: '100%',
      width: '100%',
      zIndex: 10,
      backgroundImage: `
        linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(0, 0, 0, 0.3) 50%,
          rgba(0, 0, 0, 0.6) 100%
        );
      `,
    },
    views: {
      position: 'absolute',
      top: 8,
      left: 8,
      zIndex: FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX + (useGradient ? 10 : 0),
    },
    bookmark: {
      position: 'absolute',
      top: useGradient ? 4 : 0,
      right: useGradient ? 4 : 0,
      zIndex: FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX + (useGradient ? 10 : 0),
    },
    textButton: useGradient
      ? {
          position: 'absolute',
          bottom: 0,
          width: 120,
          zIndex: 11,
        }
      : {},
    text: {
      padding: 12,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      fontFamily: sansSerifFontFamily,
      textAlign: 'left',
      flexShrink: 1,
      maxWidth: '100%',
      width: '100%',
    },
    brand: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: 600,
      margin: 0,
      marginBottom: 5,
      lineHeight: '1em',
      maxHeight: '1em',
      overflow: 'hidden',
      maxWidth: 'calc(100% - 24px)',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    name: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: 12,
      fontWeight: 500,
      margin: 0,
      minHeight: 0,
      overflow: 'hidden',
      lineHeight: '1em',
      maxHeight: '1em',
      maxWidth: 'calc(100% - 24px)',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })

interface LinkProps {
  navigateTo: string
}

interface ButtonProps {
  onClick: () => void
}

type Props = {
  product: ProductWithImage
  mediaId?: number
  creatorId?: number
  className?: string
  showBookmark?: boolean
  showPrice?: boolean
  showViews?: boolean
  useGradient?: boolean
  discoverySource?: ProductDiscoverySource
} & (LinkProps | ButtonProps)

const isLinkProps = (props: LinkProps | ButtonProps): props is LinkProps =>
  'navigateTo' in props

export const Product: FC<Props> = flowMax(
  addDisplayName('Product'),
  addProps((props) => ({
    linkOrButtonProps: isLinkProps(props)
      ? {
          navigateTo: props.navigateTo,
          additionalState: props.discoverySource
            ? { discoverySource: props.discoverySource }
            : {},
          showBackButton: true,
        }
      : { onClick: props.onClick },
  })),
  addOptionalIntPathParam('labelId'),
  addProps(({ labelId }) => ({
    isSearching: !!labelId,
  })),
  addProps(({ useGradient }) => ({
    classes: useStyles(!!useGradient)(),
  })),
  addTranslationHelpers,
  ({
    product: { id, image, brandName, name, price, matchesSearch, views },
    mediaId,
    creatorId,
    linkOrButtonProps,
    showBookmark,
    showPrice,
    showViews,
    useGradient,
    isSearching,
    className,
    classes,
    t,
  }) => {
    showViews = showViews ?? true
    return (
      <div
        className={cx(
          classes.container,
          isSearching && matchesSearch && classes.matchesSearch,
          className
        )}
      >
        <LinkOrButton {...linkOrButtonProps}>
          <div
            className={classes.imageContainer}
            style={
              useGradient
                ? {
                    backgroundImage: `url(${image.thumbnail})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                  }
                : {}
            }
          >
            <img
              src={image.thumbnail}
              alt={t('feed.productImageAlt', { brand: brandName, name })}
              className={classes.image}
            />
          </div>
          {useGradient && <div className={classes.imageGradient} />}
        </LinkOrButton>
        {showViews && <Views count={views ?? 0} className={classes.views} />}
        {showBookmark && (
          <BookmarkButton
            mode="productTile"
            productId={id}
            mediaId={mediaId}
            creatorId={creatorId}
            className={classes.bookmark}
            roundupStyle={useGradient ? 'mini' : undefined}
          />
        )}
        <LinkOrButton {...linkOrButtonProps} className={classes.textButton}>
          <div className={classes.text}>
            <p className={classes.brand}>{brandName}</p>
            <p className={classes.name}>
              {showPrice && price ? formattedUsd(price) : name}
            </p>
          </div>
        </LinkOrButton>
        {isSearching && !matchesSearch && (
          <div className={classes.nonMatchOverlay} />
        )}
      </div>
    )
  }
)
